import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { FaUserCircle, FaPlus, FaNewspaper, FaRegClock, FaTimes, 
  FaBookMedical, FaHeartbeat, FaBrain, FaLungs, FaAllergies, 
  FaBone, FaDna, FaVirus, FaMicroscope, FaHospital,
  FaStethoscope, FaSyringe, FaXRay, FaAmbulance, FaClinicMedical,
  FaHospitalUser, FaHandHoldingMedical, FaFileMedical, FaNotesMedical, FaPrescription,
  FaEdit, FaTrash, FaCheck
} from 'react-icons/fa';
import { MdArticle } from 'react-icons/md';
import { FaReact } from 'react-icons/fa';
import './articles_mn.css';

// # Article Management Component
function AdminPosts() {
  const navigate = useNavigate();
  const { user, isAdmin, userProfile } = useAuthStore();
  const [articles, setArticles] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newArticle, setNewArticle] = useState({
    title: '',
    description: '',
    url: '',
    iconName: 'FaBookMedical', // # Default icon
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // # Add edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editArticleId, setEditArticleId] = useState(null);
  const [editedArticle, setEditedArticle] = useState({
    title: '',
    description: '',
    url: '',
    iconName: '',
  });
  // # Add delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteArticleId, setDeleteArticleId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // # Icon selector state
  const [showIconSelector, setShowIconSelector] = useState(false);
  const [showEditIconSelector, setShowEditIconSelector] = useState(false);

  // # Array of medical icons with names
  const medicalIcons = [
    { name: 'FaBookMedical', component: FaBookMedical, label: 'Medical Book' },
    { name: 'FaHeartbeat', component: FaHeartbeat, label: 'Heartbeat' },
    { name: 'FaBrain', component: FaBrain, label: 'Brain' },
    { name: 'FaLungs', component: FaLungs, label: 'Lungs' },
    { name: 'FaAllergies', component: FaAllergies, label: 'Allergies' },
    { name: 'FaBone', component: FaBone, label: 'Bone' },
    { name: 'FaDna', component: FaDna, label: 'DNA' },
    { name: 'FaVirus', component: FaVirus, label: 'Virus' },
    { name: 'FaMicroscope', component: FaMicroscope, label: 'Microscope' },
    { name: 'FaHospital', component: FaHospital, label: 'Hospital' },
    { name: 'FaStethoscope', component: FaStethoscope, label: 'Stethoscope' },
    { name: 'FaSyringe', component: FaSyringe, label: 'Syringe' },
    { name: 'FaXRay', component: FaXRay, label: 'X-Ray' },
    { name: 'FaAmbulance', component: FaAmbulance, label: 'Ambulance' },
    { name: 'FaClinicMedical', component: FaClinicMedical, label: 'Clinic' },
    { name: 'FaHospitalUser', component: FaHospitalUser, label: 'Doctor' },
    { name: 'FaHandHoldingMedical', component: FaHandHoldingMedical, label: 'Medical Care' },
    { name: 'FaFileMedical', component: FaFileMedical, label: 'Medical File' },
    { name: 'FaNotesMedical', component: FaNotesMedical, label: 'Medical Notes' },
    { name: 'FaPrescription', component: FaPrescription, label: 'Prescription' },
    { name: 'MdArticle', component: MdArticle, label: 'Article' }
  ];

  // # Helper function to get icon component by name
  const getIconComponentByName = (name) => {
    const icon = medicalIcons.find(icon => icon.name === name);
    return icon ? icon.component : FaBookMedical; // # Default to FaBookMedical if not found
  };

  // # Handler for icon selection
  const handleIconSelect = (iconName) => {
    setNewArticle(prev => ({
      ...prev,
      iconName
    }));
    setShowIconSelector(false);
  };

  // # Handler for edit icon selection
  const handleEditIconSelect = (iconName) => {
    setEditedArticle(prev => ({
      ...prev,
      iconName
    }));
    setShowEditIconSelector(false);
  };

  // # Load theme preference and articles on mount
  useEffect(() => {
    const loadThemeAndArticles = async () => {
      try {
        // # Load theme preference
        if (user?.uid) {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setIsDarkMode(userData.themes?.gen_theme === 'dark');
          }
        }

        // # Load articles
        const technicalsDoc = await getDoc(doc(db, 'technicals', 'articles'));
        if (technicalsDoc.exists()) {
          const articlesData = technicalsDoc.data() || {};
          // # Add icon component to each article
          const articlesWithIcons = Object.entries(articlesData).reduce((acc, [id, article]) => {
            const IconComponent = article.iconName ? 
              getIconComponentByName(article.iconName) : 
              getRandomIcon();
            
            acc[id] = {
              ...article,
              IconComponent
            };
            return acc;
          }, {});
          setArticles(articlesWithIcons);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadThemeAndArticles();
  }, [user]);

  // # Get random icon component
  const getRandomIcon = () => {
    const randomIndex = Math.floor(Math.random() * medicalIcons.length);
    return medicalIcons[randomIndex].component;
  };

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      if (user?.uid) {
        await setDoc(doc(db, 'users', user.uid), {
          themes: { gen_theme: newTheme ? 'dark' : 'light' }
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error updating theme:', error);
    }
  };

  // # Handle article click - update to use navigation
  const handleArticleClick = (articleId, e) => {
    // # Prevent navigation when clicking admin controls
    if (e.target.closest('.artcl_mn_admin-controls')) {
      e.stopPropagation();
      return;
    }
    navigate(`/articles/${articleId}`);
  };

  // # Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // # Handle add modal open/close
  const handleAddArticle = () => {
    setShowAddModal(true);
    setError(null);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewArticle({ 
      title: '', 
      description: '', 
      url: '', 
      iconName: 'FaBookMedical' 
    });
    setShowIconSelector(false);
    setError(null);
  };

  // # Handle edit functions
  const handleEditClick = (articleId, e) => {
    e.stopPropagation();
    setEditArticleId(articleId);
    const article = articles[articleId];
    setEditedArticle({
      title: article.title || '',
      description: article.description || '',
      url: article.url || '',
      iconName: article.iconName || 'FaBookMedical',
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditArticleId(null);
    setEditedArticle({ 
      title: '', 
      description: '', 
      url: '', 
      iconName: '' 
    });
    setShowEditIconSelector(false);
  };

  // # Handle delete functions
  const handleDeleteClick = (articleId, e) => {
    e.stopPropagation();
    setDeleteArticleId(articleId);
    setShowDeleteConfirm(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteConfirm(false);
    setDeleteArticleId(null);
  };

  // # Handle input changes for add/edit
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewArticle(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditedArticle(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // # Handle article submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // # Validate inputs
      if (!newArticle.title.trim() || !newArticle.description.trim() || !newArticle.url.trim()) {
        throw new Error('Please fill in all fields');
      }

      // # Create article ID
      const timestamp = Date.now();
      const uniqueId = Math.random().toString(36).substr(2, 10);
      const articleId = `artcl-${timestamp}-${uniqueId}`;

      // # Create article data
      const articleData = {
        ...newArticle,
        authorName: userProfile?.firstName ? `By Dr. ${userProfile.firstName} ${userProfile.lastName || ''}` : user.email,
        authorPhotoURL: userProfile?.photoURL || null,
        createdAt: timestamp,
      };

      // # Update Firestore
      const technicalsRef = doc(db, 'technicals', 'articles');
      await setDoc(technicalsRef, {
        [articleId]: articleData
      }, { merge: true });

      // # Update local state
      setArticles(prev => ({
        ...prev,
        [articleId]: {
          ...articleData,
          IconComponent: getIconComponentByName(articleData.iconName)
        }
      }));

      // # Close modal and reset form
      handleCloseModal();
    } catch (error) {
      console.error('Error creating article:', error);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // # Handle article update
  const handleUpdateArticle = async (e) => {
    e.preventDefault();
    if (!isAdmin || !editArticleId) return;

    try {
      setIsSubmitting(true);

      // # Validate inputs
      if (!editedArticle.title.trim() || !editedArticle.description.trim() || !editedArticle.url.trim()) {
        throw new Error('Please fill in all fields');
      }

      const currentArticle = articles[editArticleId];

      // # Create updated article data
      const updatedArticleData = {
        ...currentArticle,
        title: editedArticle.title,
        description: editedArticle.description,
        url: editedArticle.url,
        iconName: editedArticle.iconName,
        updatedAt: Date.now(),
      };

      // # Update Firestore
      const technicalsRef = doc(db, 'technicals', 'articles');
      await setDoc(technicalsRef, {
        [editArticleId]: updatedArticleData
      }, { merge: true });

      // # Update local state
      setArticles(prev => ({
        ...prev,
        [editArticleId]: {
          ...updatedArticleData,
          IconComponent: getIconComponentByName(editedArticle.iconName)
        }
      }));

      // # Close modal
      handleCloseEditModal();
    } catch (error) {
      console.error('Error updating article:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // # Handle article deletion
  const handleDeleteArticle = async () => {
    if (!isAdmin || !deleteArticleId) return;

    try {
      setIsDeleting(true);
      
      // # Get current articles
      const technicalsRef = doc(db, 'technicals', 'articles');
      const technicalsDoc = await getDoc(technicalsRef);
      
      if (technicalsDoc.exists()) {
        const articlesData = technicalsDoc.data();
        
        // # Create a copy and remove the article
        const updatedArticles = { ...articlesData };
        delete updatedArticles[deleteArticleId];
        
        // # Update Firestore without the deleted article
        await setDoc(technicalsRef, updatedArticles);
        
        // # Update local state
        const updatedLocalArticles = { ...articles };
        delete updatedLocalArticles[deleteArticleId];
        setArticles(updatedLocalArticles);
        
        // # Close modal
        handleCloseDeleteModal();
      }
    } catch (error) {
      console.error('Error deleting article:', error);
      alert(`Error: ${error.message}`);
      handleCloseDeleteModal();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={`artcl_mn_container ${isDarkMode ? 'artcl_mn_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <main className="artcl_mn_content">
        {/* # Hero Section */}
        <section className="artcl_mn_hero">
          <div className="artcl_mn_hero-content">
            <h1>
              <MdArticle className="artcl_mn_hero-icon" />
              Articles
            </h1>
            <p>Latest USMLE preparation news, tips and tricks</p>
            {isAdmin && (
              <button className="artcl_mn_add-article-btn" onClick={handleAddArticle}>
                <FaPlus /> Add New Article
              </button>
            )}
          </div>
        </section>

        {/* # Articles Grid */}
        <section className="artcl_mn_articles-section">
          {isLoading ? (
            <div className="artcl_mn_loading">
              <FaRegClock className="artcl_mn_loading-icon" />
              Loading articles...
            </div>
          ) : Object.keys(articles).length === 0 ? (
            <div className="artcl_mn_no-articles">
              <FaNewspaper className="artcl_mn_no-articles-icon" />
              <h2>No Articles Yet</h2>
              <p>Stay tuned for upcoming articles and insights.</p>
              {isAdmin && (
                <button className="artcl_mn_add-article-btn" onClick={handleAddArticle}>
                  <FaPlus /> Add New Article
                </button>
              )}
            </div>
          ) : (
            <div className="artcl_mn_articles-grid">
              {Object.entries(articles).map(([articleId, article]) => {
                const IconComponent = article.IconComponent;
                return (
                  <article 
                    key={articleId} 
                    className="artcl_mn_article-card"
                    onClick={(e) => handleArticleClick(articleId, e)}
                  >
                    <div className="artcl_mn_article-content">
                      <div className="artcl_mn_article-header">
                        <IconComponent className="artcl_mn_article-icon" />
                        <h3>{article.title}</h3>
                        
                        {/* # Admin controls */}
                        {isAdmin && (
                          <div className="artcl_mn_admin-controls">
                            <button 
                              className="artcl_mn_edit-button" 
                              onClick={(e) => handleEditClick(articleId, e)}
                              aria-label="Edit article"
                            >
                              <FaEdit />
                            </button>
                            <button 
                              className="artcl_mn_delete-button" 
                              onClick={(e) => handleDeleteClick(articleId, e)}
                              aria-label="Delete article"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        )}
                      </div>
                      <p className="artcl_mn_article-description">
                        {article.description}
                      </p>
                    </div>
                    <div className="artcl_mn_article-footer">
                      <div className="artcl_mn_author-info">
                        {article.authorPhotoURL ? (
                          <img 
                            src={article.authorPhotoURL} 
                            alt={article.authorName} 
                            className="artcl_mn_author-avatar"
                          />
                        ) : (
                          <FaUserCircle className="artcl_mn_author-avatar-fallback" />
                        )}
                        <div className="artcl_mn_author-details">
                          <span className="artcl_mn_author-name">{article.authorName}</span>
                          <span className="artcl_mn_article-date">
                            {formatDate(article.createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* # Add Article Modal */}
      {showAddModal && (
        <div className="artcl_mn_modal-overlay" onClick={handleCloseModal}>
          <div className="artcl_mn_modal" onClick={e => e.stopPropagation()}>
            <button className="artcl_mn_modal-close" onClick={handleCloseModal}>
              <FaTimes />
            </button>
            <h2>Add New Article</h2>
            {error && <div className="artcl_mn_modal-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="artcl_mn_form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={newArticle.title}
                  onChange={handleInputChange}
                  placeholder="Enter article title"
                  required
                />
              </div>
              <div className="artcl_mn_form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={newArticle.description}
                  onChange={handleInputChange}
                  placeholder="Enter article description"
                  required
                />
              </div>
              <div className="artcl_mn_form-group">
                <label htmlFor="url">Notion Embed URL</label>
                <input
                  type="url"
                  id="url"
                  name="url"
                  value={newArticle.url}
                  onChange={handleInputChange}
                  placeholder="Enter Notion embed URL"
                  required
                />
              </div>
              
              {/* # Icon Selector Button */}
              <div className="artcl_mn_form-group">
                <label>Choose Article Icon</label>
                <div className="artcl_mn_icon-selector-preview" onClick={() => setShowIconSelector(!showIconSelector)}>
                  {React.createElement(getIconComponentByName(newArticle.iconName), { className: "artcl_mn_selected-icon" })}
                  <span className="artcl_mn_selected-icon-name">
                    {medicalIcons.find(icon => icon.name === newArticle.iconName)?.label || 'Select Icon'}
                  </span>
                </div>
                
                {/* # Icon Grid */}
                {showIconSelector && (
                  <div className="artcl_mn_icon-grid">
                    {medicalIcons.map(icon => (
                      <div 
                        key={icon.name}
                        className={`artcl_mn_icon-option ${newArticle.iconName === icon.name ? 'artcl_mn_selected' : ''}`}
                        onClick={() => handleIconSelect(icon.name)}
                      >
                        {React.createElement(icon.component, { className: "artcl_mn_icon-option-img" })}
                        <span className="artcl_mn_icon-option-name">{icon.label}</span>
                        {newArticle.iconName === icon.name && (
                          <FaCheck className="artcl_mn_icon-selected-check" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <button 
                type="submit" 
                className="artcl_mn_submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating...' : 'Create Article'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* # Edit Article Modal */}
      {showEditModal && (
        <div className="artcl_mn_modal-overlay" onClick={handleCloseEditModal}>
          <div className="artcl_mn_modal" onClick={e => e.stopPropagation()}>
            <button className="artcl_mn_modal-close" onClick={handleCloseEditModal}>
              <FaTimes />
            </button>
            <h2>Edit Article</h2>
            <form onSubmit={handleUpdateArticle}>
              <div className="artcl_mn_form-group">
                <label htmlFor="edit-title">Title</label>
                <input
                  type="text"
                  id="edit-title"
                  name="title"
                  value={editedArticle.title}
                  onChange={handleEditInputChange}
                  placeholder="Enter article title"
                  required
                />
              </div>
              <div className="artcl_mn_form-group">
                <label htmlFor="edit-description">Description</label>
                <textarea
                  id="edit-description"
                  name="description"
                  value={editedArticle.description}
                  onChange={handleEditInputChange}
                  placeholder="Enter article description"
                  required
                />
              </div>
              <div className="artcl_mn_form-group">
                <label htmlFor="edit-url">Notion Embed URL</label>
                <input
                  type="url"
                  id="edit-url"
                  name="url"
                  value={editedArticle.url}
                  onChange={handleEditInputChange}
                  placeholder="Enter Notion embed URL"
                  required
                />
              </div>
              
              {/* # Edit Icon Selector */}
              <div className="artcl_mn_form-group">
                <label>Choose Article Icon</label>
                <div className="artcl_mn_icon-selector-preview" onClick={() => setShowEditIconSelector(!showEditIconSelector)}>
                  {React.createElement(getIconComponentByName(editedArticle.iconName), { className: "artcl_mn_selected-icon" })}
                  <span className="artcl_mn_selected-icon-name">
                    {medicalIcons.find(icon => icon.name === editedArticle.iconName)?.label || 'Select Icon'}
                  </span>
                </div>
                
                {/* # Edit Icon Grid */}
                {showEditIconSelector && (
                  <div className="artcl_mn_icon-grid">
                    {medicalIcons.map(icon => (
                      <div 
                        key={icon.name}
                        className={`artcl_mn_icon-option ${editedArticle.iconName === icon.name ? 'artcl_mn_selected' : ''}`}
                        onClick={() => handleEditIconSelect(icon.name)}
                      >
                        {React.createElement(icon.component, { className: "artcl_mn_icon-option-img" })}
                        <span className="artcl_mn_icon-option-name">{icon.label}</span>
                        {editedArticle.iconName === icon.name && (
                          <FaCheck className="artcl_mn_icon-selected-check" />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <button 
                type="submit" 
                className="artcl_mn_submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Updating...' : 'Update Article'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* # Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="artcl_mn_modal-overlay" onClick={handleCloseDeleteModal}>
          <div className="artcl_mn_modal artcl_mn_delete-modal" onClick={e => e.stopPropagation()}>
            <h2>Delete Article?</h2>
            <p>Are you sure you want to delete this article? This action cannot be undone.</p>
            <div className="artcl_mn_delete-actions">
              <button 
                className="artcl_mn_cancel-btn"
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button 
                className="artcl_mn_delete-confirm-btn"
                onClick={handleDeleteArticle}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default AdminPosts;
